import React, { useEffect, useState } from 'react';
import type { SkillCheckResultDTO } from '@hexcrawl/shared-utils';

interface Props {
  result: SkillCheckResultDTO;
  onDismiss: () => void;
}

import { ATTRIBUTE_ICONS as ATTR_EMOJIS, ATTRIBUTE_LABELS as ATTR_LABELS, SKILL_ICONS as SKILL_EMOJIS, SKILL_LABELS } from '../../constants/entityDisplay';

const SPIN_DURATION_MS = 900;
const SPIN_INTERVAL_MS = 60;
const REVEAL_DELAY_MS = 300;
const DICE_MAX = 20;

export const SkillCheckResult: React.FC<Props> = ({ result, onDismiss }) => {
  const [displayRoll, setDisplayRoll] = useState<number>(1);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    setDisplayRoll(1);
    setRevealed(false);

    const steps = Math.floor(SPIN_DURATION_MS / SPIN_INTERVAL_MS);
    let step = 0;

    const timer = setInterval(() => {
      step += 1;
      if (step >= steps) {
        clearInterval(timer);
        setDisplayRoll(result.roll);
        setTimeout(() => setRevealed(true), REVEAL_DELAY_MS);
      } else {
        setDisplayRoll(Math.floor(Math.random() * DICE_MAX) + 1);
      }
    }, SPIN_INTERVAL_MS);

    return () => clearInterval(timer);
  }, [result]);

  const attrEmoji = ATTR_EMOJIS[result.attribute] ?? '';
  const attrLabel = ATTR_LABELS[result.attribute] ?? result.attribute;
  const skillEmoji = SKILL_EMOJIS[result.skill] ?? '';
  const skillLabel = SKILL_LABELS[result.skill] ?? result.skill;
  const successColor = result.success ? '#66ff99' : '#ff6666';
  const outcomeText = result.success ? 'SUCCESS' : 'FAILURE';
  const borderColor = revealed ? successColor : '#4a6080';

  const modSign = result.modifier >= 0 ? '+' : '';

  return (
    <div style={{
      background: '#0a1a2a',
      border: `2px solid ${borderColor}`,
      borderRadius: 10,
      padding: '20px 28px',
      textAlign: 'center',
      fontFamily: 'monospace',
      width: 260,
      boxSizing: 'border-box',
    }}>
      <div style={{ color: '#ffcc66', fontSize: 16, fontWeight: 'bold', marginBottom: 4 }}>
        {attrEmoji} {attrLabel} + {skillEmoji} {skillLabel}
      </div>
      <div style={{ color: '#888', fontSize: 13, marginBottom: 16 }}>
        DC {result.dc}
      </div>

      <div style={{
        fontSize: 48,
        fontWeight: 'bold',
        color: '#ffffff',
        lineHeight: 1,
        marginBottom: 8,
      }}>
        {displayRoll}
      </div>

      <div style={{ color: '#aaa', fontSize: 13, marginBottom: 12, visibility: revealed ? 'visible' : 'hidden' }}>
        {result.roll} {modSign}{result.modifier} = {result.total}
      </div>
      <div style={{
        color: successColor,
        fontSize: 22,
        fontWeight: 'bold',
        letterSpacing: 2,
        marginBottom: 20,
        visibility: revealed ? 'visible' : 'hidden',
      }}>
        {outcomeText}
      </div>
      <button
        onClick={onDismiss}
        disabled={!revealed}
        style={{
          background: 'transparent',
          color: revealed ? '#a8d1ff' : 'transparent',
          border: `1px solid ${revealed ? '#5aa0ff' : 'transparent'}`,
          borderRadius: 6,
          padding: '6px 28px',
          fontFamily: 'monospace',
          fontSize: 13,
          cursor: revealed ? 'pointer' : 'default',
        }}
      >
        Continue
      </button>
    </div>
  );
};
